<script lang="ts" setup>
import { useLanguage } from '#composables/useLanguage';
import type { Dayjs } from 'dayjs';
import { CInputText } from '@carrefour/design-system-components-vue3';
import { ref, watch } from 'vue';
import { INPUT_DATE_FORMAT } from './constants/periodDateInput.constant';
import type { PeriodDateInputsPropsType } from './types/periodDateInputsProps.type';
import { formatInputDate, parseInputDate } from './utils/periodDateInput.utils';

const { name, start, end, disabled = false } = defineProps<PeriodDateInputsPropsType>();

const emit = defineEmits<{
  'update:start': [date: Dayjs];
  'update:end': [date: Dayjs];
}>();

const { t } = useLanguage();

const startText = ref('');
const endText = ref('');

watch(
  () => start,
  (date) => (startText.value = date ? formatInputDate(date) : ''),
  { immediate: true }
);
watch(
  () => end,
  (date) => (endText.value = date ? formatInputDate(date) : ''),
  { immediate: true }
);

// On a valid entry, notify the parent; otherwise silently fall back to the last known-good value.
function commitStart() {
  const parsed = parseInputDate(startText.value);

  if (parsed) {
    emit('update:start', parsed);
  } else {
    startText.value = start ? formatInputDate(start) : '';
  }
}

function commitEnd() {
  const parsed = parseInputDate(endText.value);

  if (parsed) {
    emit('update:end', parsed);
  } else {
    endText.value = end ? formatInputDate(end) : '';
  }
}
</script>

<template>
  <div class="grid grid-cols-2 gap-3 min-w-0">
    <!--
      min-w-0: without it, CSS Grid's automatic minimum size keeps a track as wide as its
      item's min-content, so the cell never shrinks.
      [&_input]:min-w-0 [&_input]:!w-full: the design system sets `.c-base-input__input { min-width: min-content }`,
      which overflows a narrow cell; forced width ensures the input respects its container.
    -->
    <div :data-testid="`${name}-start-input`" class="min-w-0 [&_input]:min-w-0 [&_input]:!w-full">
      <CInputText
        :model-value="startText"
        :label="t('filters.date.from')"
        :placeholder="INPUT_DATE_FORMAT"
        size="s"
        :disabled="disabled"
        @update:model-value="(value) => (startText = String(value))"
        @blur="commitStart"
        @keydown.enter="commitStart"
      />
    </div>

    <div :data-testid="`${name}-end-input`" class="min-w-0 [&_input]:min-w-0 [&_input]:!w-full">
      <CInputText
        :model-value="endText"
        :label="t('filters.date.to')"
        :placeholder="INPUT_DATE_FORMAT"
        size="s"
        :disabled="disabled"
        @update:model-value="(value) => (endText = String(value))"
        @blur="commitEnd"
        @keydown.enter="commitEnd"
      />
    </div>
  </div>
</template>
